import React, { useState, useEffect, useRef } from 'react';
import * as R from 'ramda';
import { Controller } from 'react-hook-form';
import {
  TextField,
  CircularProgress,
  Divider,
  FormGroup,
  FormControlLabel,
  InputLabel,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Typography,
  Checkbox,
  Tooltip,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

import Delete from '@material-ui/icons/Delete';
import { useTranslation } from 'react-i18next';
import { WarningMessage } from '@components/WarningMessage';

import { ChangeOrderButton } from '@views/cases/components/ChangeOrderButton';
import { ARR, OBJ, getUrnoidToPropFunc } from '@utils/data-utils';

const emailUrnoid = 'urn:oid:0.9.2342.19200300.100.1.3';
const personalNumberUrnoid = 'urn:oid:1.2.752.29.4.13';

export const Signer = ({
  name,
  existingEmail,
  existingSignerName = '',
  existingSignerPersonalID = '',
  index = 0,
  classes,
  move,
  remove,
  register,
  totalCount,
  errors = OBJ,
  idps = ARR,
  disabledCheckbox = false,
  setValue,
  getValues,
  repeatedEmailErrors,
  signed = false,
  disableArrows = { up: false, down: false },
  personalNumberCheckbox,
  control,
  idpsForSigner,
}) => {
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const isEmailError = R.has('email', errors);
  const isPersonalNumberError = R.has('personalID', errors);
  const isCheckboxError = R.has('idp', errors);
  const emailName = `${name}.email`;
  const nameName = `${name}.name`;
  const { features } = theme;
  const personalNumberOption =
    features && 'personalNumberInCases' in features ? features.personalNumberInCases : null;

  const [requirePersonalNumber, setRequirePersonalNumber] = useState(!!existingSignerPersonalID);

  const signerNameFeature =
    features && 'useSignerName' in features ? features.useSignerName : false;

  const [signer, setSigner] = useState(null);
  const emailInputRef = useRef(null);
  const checkboxRefs = useRef([]);

  const updateSigner = () => {
    const values = getValues();
    if (values && values.signers && values.signers.length > index)
      setSigner({ ...values.signers[index] });
  };

  const setIdpValue = (id, val) => {
    setValue(`${name}.idpsForSigner.${id}`, val);
    updateSigner();
  };

  const setEmailValue = (val) => {
    setValue(`${name}.email`, val);
    updateSigner();
  };

  const setSignerNameValue = (val) => {
    setValue(`${name}.name`, val);
    updateSigner();
  };

  const setPersonalNumberCheckboxValue = (val) => {
    setValue(`${name}.personalNumberCheckbox`, val);
    updateSigner();
  };

  useEffect(() => {
    setPersonalNumberCheckboxValue(personalNumberCheckbox);
  }, [personalNumberCheckbox]);

  useEffect(() => {
    setEmailValue(existingEmail);
  }, [existingEmail]);

  useEffect(() => {
    setSignerNameValue(existingSignerName);
  }, [existingSignerName]);

  useEffect(() => {
    if (idps.length) {
      idps.forEach((idp) => {
        if (!idp.deselected) {
          setIdpValue(idp.id, true);
        }
      });
    }
    updateSigner();
  }, [idps]);

  useEffect(() => {
    setRequirePersonalNumber(!!existingSignerPersonalID);
    setValue(`${name}.personalID`, existingSignerPersonalID);
    updateSigner();
  }, [existingSignerPersonalID]);

  useEffect(() => {
    setRequirePersonalNumber(personalNumberCheckbox);
    setPersonalNumberCheckboxValue(personalNumberCheckbox);
    updateSigner();
  }, [personalNumberCheckbox]);
  // update form if idpscheckBox states changes
  useEffect(() => {
    if (!idpsForSigner || Object.keys(idpsForSigner).length < 1) return;
    Object.keys(idpsForSigner).forEach((idpName) => setIdpValue(idpName, !!idpsForSigner[idpName]));
    updateSigner();
  }, [idpsForSigner]);

  const requirePersonalNumberChange = (event, val) => {
    setRequirePersonalNumber(val);
    setValue(`${name}.personalNumberCheckbox`, val);
    if (val) {
      idps.forEach((idp) => {
        if (!idp.containsPersonalNumber) {
          setValue(`${name}.idpsForSigner.${idp.id}`, false);
        }
      });
    }
    updateSigner();
  };

  const { label: emailLabel, validation: emailValidation } = getUrnoidToPropFunc(emailUrnoid)(t);
  const { label: personalNumberLabel, validation: personalNumberValidate } = getUrnoidToPropFunc(
    personalNumberUrnoid,
  )(t);

  const languagePrefix = i18n.language.split('-')[0];
  const checkKeyDown = (e) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  useEffect(() => {
    if (repeatedEmailErrors && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [repeatedEmailErrors, index]);

  useEffect(() => {
    if (isCheckboxError && checkboxRefs.current.length > 0) {
      // Focus the first checkbox (or the one you want)
      checkboxRefs.current[0]?.focus();
    }
  }, [isCheckboxError]);

  return (
    <Card className={classes.box}>
      <CardContent>
        <Typography variant="subtitle1" component="h2" className={classes.uppercase}>
          {t('Signer details', { index: totalCount < 2 ? undefined : index + 1 })}
        </Typography>
        <Divider />
        <InputLabel htmlFor={`signer-${index}-email`} className={classes.subtitle} required>
          {emailLabel}
        </InputLabel>
        <Controller
          name={emailName}
          defaultValue={signer?.email}
          control={control}
          rules={{ ...emailValidation }}
          render={({ value, ref, onChange }) => (
            <TextField
              value={value}
              disabled={signed}
              id={`signer-${index}-email`}
              inputRef={(node) => {
                emailInputRef.current = node;
                if (typeof ref === 'function') {
                  ref(node);
                } else if (ref && typeof ref === 'object') {
                  const refObj = ref;
                  refObj.current = node;
                }
              }}
              fullWidth
              helperText={
                isEmailError || repeatedEmailErrors
                  ? null
                  : t('Please make sure you type the correct address')
              }
              error={isEmailError || repeatedEmailErrors}
              onChange={(e) => {
                const val = e.target.value.trim();
                onChange(val);
              }}
              inputProps={{
                maxLength: 254, // RFC 5321 standard
                'aria-required': 'true',
                'aria-describedby': isEmailError
                  ? 'email-error'
                  : repeatedEmailErrors
                  ? 'duplicate-message-error'
                  : undefined,
              }}
              onKeyDown={(e) => checkKeyDown(e)}
            />
          )}
        />
        {isEmailError && (
          <Typography id="email-error" color="error" role="alert" aria-live="assertive">
            {errors.email.message}
          </Typography>
        )}
        {repeatedEmailErrors && (
          <Typography id="duplicate-email-error" color="error" role="alert" aria-live="assertive">
            {repeatedEmailErrors}
          </Typography>
        )}
        {signerNameFeature && (
          <>
            <InputLabel htmlFor="name" className={classes.subtitle}>
              {t('Name (firstname lastname) (optional)')}
            </InputLabel>
            <TextField
              disabled={signed}
              name={nameName}
              id="name"
              inputRef={register()}
              fullWidth
              defaultValue={signer?.name}
              helperText=""
              inputProps={{
                maxLength: 254, // RFC 5321 standard
              }}
              onKeyDown={(e) => checkKeyDown(e)}
            />
          </>
        )}
        {personalNumberOption && (
          <div className={{ ...classes.box, ...classes.personalIdCheckbox }}>
            <FormControlLabel
              control={
                <Controller
                  name={`${name}.personalNumberCheckbox`}
                  control={control}
                  defaultValue={signer?.personalNumberCheckbox}
                  render={({ value, ref, onChange }) => (
                    <Checkbox
                      size="medium"
                      inputRef={ref}
                      checked={value}
                      disabled={disabledCheckbox}
                      onChange={(e, val) => {
                        requirePersonalNumberChange(e, val);
                        onChange(e.target.checked);
                      }}
                    />
                  )}
                />
              }
              label={t('Require personal number')}
            />
          </div>
        )}
        {requirePersonalNumber && (
          <>
            <InputLabel htmlFor="personalID" className={classes.subtitle} required>
              {personalNumberLabel}
            </InputLabel>
            <Controller
              name={`${name}.personalID`}
              defaultValue={signer?.personalID ?? ''}
              control={control}
              rules={{ ...personalNumberValidate }}
              render={({ value, ref, onChange }) => (
                <TextField
                  value={value}
                  disabled={signed}
                  id="personalID"
                  inputRef={ref}
                  fullWidth
                  autoFocus
                  error={isPersonalNumberError}
                  onKeyDown={(e) => checkKeyDown(e)}
                  onChange={(ev) => {
                    const val = ev.target.value.trim();
                    onChange(val);
                  }}
                  inputProps={{
                    'aria-describedby': isPersonalNumberError ? 'personal-number-error' : undefined,
                  }}
                />
              )}
            />

            {isPersonalNumberError && (
              <Typography
                id="personal-number-error"
                color="error"
                role="alert"
                aria-live="assertive"
              >
                {errors.personalID.message}
              </Typography>
            )}
          </>
        )}
        <FormGroup row id="method">
          {!R.isEmpty(idps) ? (
            idps.map((idp, idx) => (
              <FormControlLabel
                key={idp.id}
                control={
                  <Controller
                    name={`${name}.idpsForSigner.${idp.id}`}
                    control={control}
                    defaultValue={!!signer?.[idp.id]}
                    render={({ value, ref, onChange }) => (
                      <Checkbox
                        size="medium"
                        inputRef={(node) => {
                          // Save ref for focusing
                          checkboxRefs.current[idx] = node;
                          if (typeof ref === 'function') {
                            ref(node);
                          } else if (ref && typeof ref === 'object') {
                            const refObj = ref;
                            refObj.current = node;
                          }
                        }}
                        checked={value}
                        disabled={
                          (requirePersonalNumber && !idp.containsPersonalNumber) || disabledCheckbox
                        }
                        onChange={(e, val) => {
                          setIdpValue(idp.id, val);
                          onChange(e.target.checked);
                        }}
                        inputProps={{
                          'aria-describedby': isCheckboxError ? 'checkbox-error' : undefined,
                        }}
                      />
                    )}
                  />
                }
                label={
                  idp[`displayName_${languagePrefix}`]
                    ? idp[`displayName_${languagePrefix}`]
                    : idp.displayName
                }
                disabled={
                  (requirePersonalNumber && !idp.containsPersonalNumber) || disabledCheckbox
                }
              />
            ))
          ) : (
            <CircularProgress />
          )}
        </FormGroup>
        {signer && !!signer?.idpsForSigner?.SMS && !signed && (
          <WarningMessage
            message="SMS is a weaker authentication method"
            role="alert"
            aria-live="polite"
          />
        )}
        {isCheckboxError && (
          <Typography id="checkbox-error" color="error" role="alert" aria-live="assertive">
            {errors.idp.message}
          </Typography>
        )}
      </CardContent>
      <CardActions>
        {index !== totalCount - 1 && !signed && !disableArrows.down && (
          <ChangeOrderButton onClick={() => move(index, index + 1)} ariaLabel={t('Move down')} />
        )}
        {index !== 0 && !disableArrows.up && (
          <ChangeOrderButton
            direction="up"
            onClick={() => move(index, index - 1)}
            ariaLabel={t('Move Up')}
          />
        )}
        {totalCount !== 1 && !signed && (
          <Tooltip title={t('Delete')}>
            <IconButton onClick={() => remove(index)} aria-label={t('Delete')}>
              <Delete />
            </IconButton>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
};
